import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Comic,
  FORMAT_LABELS,
  LibraryTile,
  READ_STATUS_LABELS,
  completeLabel,
  nextReadLabel,
  progressPercent,
  unitLabel,
} from '../types';
import { gradients } from '../../../theme/colors';
import { cardFoot, footPink, footSecondary } from '../../../utils/surfaces';
import CoverImage from './CoverImage';
import ReadStateIcon from './ReadStateIcon';
import { DragHandle, sameHandle, useSortableCard } from './useSortableCard';

export interface TileProgress {
  read: number;
  total: number;
  // Marked started, not read yet
  started?: number;
}

interface ShelfTileProps {
  tile: LibraryTile;
  // Which shelf it sits on; drags only land on the same shelf
  container: string;
  coverUrl?: string;
  progress?: TileProgress;
  isReadOnly: boolean;
  draggable: boolean;
  // Narrow phone tiles: the chip moves to the bottom so it can't hit the buttons
  compact?: boolean;
  onOpen: (tile: LibraryTile) => void;
  onMenuClick: (e: React.MouseEvent<HTMLButtonElement>, tile: LibraryTile) => void;
  // Standalone comics get the read eye; series use their checklist
  onToggleRead?: (comic: Comic) => void;
  // Replaces the right of the second line, e.g. "Next: #9" on the Reading tab
  detail?: string;
}

const overlayButton = {
  position: 'absolute',
  top: 4,
  width: 30,
  height: 30,
  bgcolor: 'rgba(0,0,0,0.55)',
  color: 'common.white',
  backdropFilter: 'blur(4px)',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
} as const;

const tileTitle = (tile: LibraryTile) =>
  tile.kind === 'series'
    ? [tile.series.title, tile.series.startYear ? `(${tile.series.startYear})` : '']
        .filter(Boolean)
        .join(' ')
    : tile.comic.title;

const chipLabel = (tile: LibraryTile) =>
  tile.kind === 'series'
    ? tile.series.trackBy === 'issue'
      ? 'Issues'
      : 'TPB'
    : FORMAT_LABELS[tile.comic.format];

// Second line: year (or publisher) on the left, a short note on the right
const metaLine = (tile: LibraryTile, total: number) => {
  if (tile.kind === 'comic') {
    return { left: tile.comic.publisher || '', right: '' };
  }
  const { series } = tile;
  return {
    left: series.startYear ? String(series.startYear) : series.publisher || '',
    right: series.ongoing && !series.totalCount ? 'Ongoing' : total > 0 ? `${total} ${unitLabel(series.trackBy, total)}` : '',
  };
};

// The pink line: where I am with it, and the count on the right
const statusLine = (tile: LibraryTile, progress?: TileProgress) => {
  if (tile.kind === 'comic') {
    const { owned, readStatus } = tile.comic;
    return {
      status: READ_STATUS_LABELS[readStatus],
      count: owned && readStatus !== 'wantToRead' ? 'Owned' : '',
    };
  }
  const { read = 0, total = 0, started = 0 } = progress ?? {};
  const status = tile.series.complete
    ? `✓ ${completeLabel(tile.series)}`
    : read === 0 && started === 0
    ? 'Not started'
    : total > 0 && read >= total
    ? 'All read'
    : 'Reading';
  return { status, count: total > 0 ? `${read}/${total}` : `${read} read` };
};

type TileContentProps = Omit<ShelfTileProps, 'container'> & { handle: DragHandle };

// Everything inside a tile, apart from its drag wrapper (see useSortableCard).
const TileContentView = ({
  tile,
  coverUrl,
  progress,
  isReadOnly,
  draggable,
  compact = false,
  onOpen,
  onMenuClick,
  onToggleRead,
  detail,
  handle,
}: TileContentProps) => {
  const { attributes, listeners, setActivatorNodeRef } = handle;
  const title = tileTitle(tile);
  const percent = progress ? progressPercent(progress.read, progress.total) : 0;
  const meta = metaLine(tile, progress?.total ?? 0);
  const { status, count } = statusLine(tile, progress);
  const right = detail ?? meta.right;

  return (
    <>
      <CoverImage src={coverUrl} title={title} onClick={() => onOpen(tile)}>
        <Box
          sx={{
            position: 'absolute',
            ...(compact && draggable
              ? { bottom: 12, left: 6 }
              : { top: 8, left: draggable ? 40 : 6 }),
            px: 1,
            py: 0.25,
            borderRadius: 999,
            bgcolor: 'rgba(0,0,0,0.6)',
            color: 'common.white',
            fontSize: 11,
            fontWeight: 800,
            pointerEvents: 'none',
          }}
        >
          {chipLabel(tile)}
        </Box>
      </CoverImage>

      {draggable && (
        <IconButton
          ref={setActivatorNodeRef}
          size="small"
          aria-label={`Drag ${title}`}
          {...attributes}
          {...listeners}
          sx={{
            ...overlayButton,
            left: 4,
            cursor: 'grab',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      )}

      {!isReadOnly && (
        <IconButton
          size="small"
          aria-label={`${title} options`}
          onClick={(e) => onMenuClick(e, tile)}
          sx={{ ...overlayButton, right: 4 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}

      <Box
        sx={(theme) => ({
          px: 1.25,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          ...cardFoot(theme),
        })}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>

        {(meta.left || right) && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
              fontSize: 12,
              color: footSecondary(theme),
            })}
          >
            <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {meta.left}
            </Box>
            <Box component="span" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              {right}
            </Box>
          </Box>
        )}

        {tile.kind === 'series' && (
          <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${percent}%`,
                height: '100%',
                backgroundImage: gradients.primary,
                backgroundSize: percent > 0 ? `${(100 / percent) * 100}% 100%` : undefined,
              }}
            />
          </Box>
        )}

        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            minHeight: 22,
            fontSize: 12,
            fontWeight: 700,
            color: footPink(theme),
          })}
        >
          <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {status}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {count && (
              <Box component="span" sx={(theme) => ({ color: footSecondary(theme), fontWeight: 600 })}>
                {count}
              </Box>
            )}
            {tile.kind === 'comic' && onToggleRead && !isReadOnly && (() => {
              const { readStatus } = tile.comic;
              const label = nextReadLabel(readStatus);
              const begun = readStatus === 'read' || readStatus === 'started';
              return (
                <Tooltip title={label}>
                  <IconButton
                    size="small"
                    aria-label={`${label}: ${title}`}
                    aria-pressed={readStatus === 'read' ? true : readStatus === 'started' ? 'mixed' : false}
                    onClick={() => onToggleRead(tile.comic)}
                    sx={(theme) => ({ p: 0.25, color: begun ? footPink(theme) : footSecondary(theme) })}
                  >
                    <ReadStateIcon status={readStatus} sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              );
            })()}
          </Box>
        </Box>
      </Box>
    </>
  );
};

// Tiles are rebuilt on every filter pass, so they're compared by what they
// point at. Callbacks are stable from the page.
const sameTile = (a: LibraryTile, b: LibraryTile) =>
  a.id === b.id &&
  (a.kind === 'series'
    ? b.kind === 'series' && a.series === b.series
    : b.kind === 'comic' && a.comic === b.comic);

const sameProgress = (a?: TileProgress, b?: TileProgress) =>
  a?.read === b?.read && a?.total === b?.total && a?.started === b?.started;

const TileContent = React.memo(TileContentView, (prev, next) => {
  const { tile: prevTile, progress: prevProgress, handle: prevHandle, ...prevRest } = prev;
  const { tile: nextTile, progress: nextProgress, handle: nextHandle, ...nextRest } = next;
  return (
    sameTile(prevTile, nextTile) &&
    sameProgress(prevProgress, nextProgress) &&
    sameHandle(prevHandle, nextHandle) &&
    (Object.keys(prevRest) as (keyof typeof prevRest)[]).every((key) => prevRest[key] === nextRest[key])
  );
});

const articleSx = {
  borderRadius: 2,
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'primary.main',
  position: 'relative',
} as const;

// One cover on a Library shelf: the drag wrapper around its content
const ShelfTile = ({ container, ...props }: ShelfTileProps) => {
  const { setNodeRef, style, handle } = useSortableCard(props.tile.id, !props.draggable, { container });
  return (
    <Box ref={setNodeRef} component="article" sx={articleSx} style={style}>
      <TileContent {...props} handle={handle} />
    </Box>
  );
};

export default ShelfTile;
